import React, { useMemo } from "react";
import { ICommandBarItemProps } from "office-ui-fabric-react";
import SelectionModeSwitchableCommandBar from "../common/SelectionModeSwitchableCommandBar";
import ListMenuFilterSelector from "../common/ListMenuFilterSelector";
import * as Metadata from "../../entities/Metadata";

interface Props {
    selectedCount: number;
    allSelectedPending: boolean;
    canRecallSelected: boolean;
    onApprove: () => void;
    onReject: () => void;
    onRecall: () => void;
    selectionCommands?: ICommandBarItemProps[];
    onCancelSelection: () => void;
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    onFilterChange: (filterId: string) => void;
    canManageConfiguration: boolean;
    onFilterAddClick?: () => void;
    onFilterEditClick?: (id: string) => void;
    onFilterRemoveClick?: (id: string) => void;
    onFilterCopyClick?: (filter: Metadata.IFilter<Metadata.BaseFilterValue>) => void;
    preFilter?: Metadata.PreFilter<any>;
    preFilterId?: string;
    isReadonlyMode?: boolean;
    onConfigureColumnsClick: () => void;
    extraItems?: ICommandBarItemProps[];
}

const ApprovalCenterCommandBar = (props: Props) => {
    const {
        selectedCount,
        allSelectedPending,
        canRecallSelected,
        onApprove,
        onReject,
        onRecall,
        selectionCommands,
        onCancelSelection,
        filters,
        activeFilter,
        onFilterChange,
        canManageConfiguration,
        onFilterAddClick,
        onFilterEditClick,
        onFilterRemoveClick,
        onFilterCopyClick,
        preFilter,
        preFilterId,
        isReadonlyMode,
        onConfigureColumnsClick,
        extraItems
    } = props;

    const normalModeItems: ICommandBarItemProps[] = [...(extraItems ?? [])];

    const farItems: ICommandBarItemProps[] = [
        {
            key: "configureColumns",
            text: "Configure Columns",
            iconProps: { iconName: "TripleColumnEdit" },
            onClick: onConfigureColumnsClick
        }
    ];

    farItems.push({
        key: "filter",
        onRender: () => (
            <ListMenuFilterSelector
                filters={filters}
                activeFilter={activeFilter}
                canManageConfiguration={canManageConfiguration}
                preFilter={preFilter}
                preFilterId={preFilterId}
                onActiveItemChanged={id => {
                    const newActiveFilter = filters.find(_ => _.id === id);
                    if (newActiveFilter) {
                        onFilterChange(id);
                    }
                }}
                onItemAddClick={onFilterAddClick}
                onItemEditClick={onFilterEditClick}
                onItemRemoveClick={onFilterRemoveClick}
                onItemCopyClick={onFilterCopyClick}
            />
        )
    });

    const selectionModeItems = useMemo(() => {
        const items: ICommandBarItemProps[] = [];

        if (allSelectedPending) {
            items.push(
                {
                    key: "approve",
                    text: "Approve",
                    iconProps: { iconName: "Completed" },
                    disabled: isReadonlyMode,
                    onClick: onApprove
                },
                {
                    key: "reject",
                    text: "Reject",
                    iconProps: { iconName: "Broom" },
                    disabled: isReadonlyMode,
                    onClick: onReject
                }
            );
        }

        if (selectionCommands) {
            items.push(...selectionCommands);
        }

        if (canRecallSelected) {
            items.push({
                key: "recall",
                text: "Recall",
                iconProps: { iconName: "Undo" },
                disabled: isReadonlyMode,
                onClick: onRecall
            });
        }

        return items;
    }, [allSelectedPending, canRecallSelected, isReadonlyMode, onApprove, onReject, onRecall, selectionCommands]);

    return (
        <SelectionModeSwitchableCommandBar
            items={normalModeItems}
            farItems={farItems}
            selectionMode={{
                enabled: selectedCount > 0,
                items: selectionModeItems,
                onCancel: onCancelSelection,
                selectedCount: selectedCount
            }}
        />
    );
};

export default ApprovalCenterCommandBar;
